/* global OT */

import timeout from '../utils/timeout';

export default class OpentokService {
    /**
     * @param {Object.<{
     *  opentokSdkUrl: string,
     *  opentokSdkLoadingTimeout: string,
     *  injectedApiUrl: string,
     *  getOpentokTokenEndpoint: string,
     *  startRecordingEndpoint: string,
     *  stopRecordingEndpoint: string,
     *  }>} config
     *  configuration
     */
    constructor(config) {
        this.config = config;
        window.Realeyesit = window.Realeyesit || {};
        const {
            Realeyesit,
        } = window;
        Realeyesit.otExtensions = Realeyesit.otExtensions || {};
        this.otExtensions = Realeyesit.otExtensions;
        this.otToken = null;
        this.publisher = null;
        this.session = null;
    }

    /**
     * Initializes the opentok service
     * @return {Promise}
     */
    init() {
        return this.loadOpentokSdk()
            .then(() => this.getOpentokToken())
            .then((token) => {
                this.otToken = token;
            })
            .then(() => {
                this.session = window.OT.initSession(this.otToken.apiKey, this.otToken.sessionId);
            });
    }

    /**
     * inject script tag to load opentok SDK and resolves when loaded or rejects when timed out
     * @return {Promise}
     */
    loadOpentokSdk() {
        return timeout(this.config.opentokSdkLoadingTimeout, new Promise((resolve, reject) => {
            // loads the Opentok SDK
            if (this.config.opentokSdkUrl) {
                /* istanbul ignore next
                   -- reason: was unable to simulate different HTML structures in the test framework */
                const head = document.head || document.getElementsByTagName('head')[0] || document.documentElement;
                const script = document.createElement('script');
                script.src = this.config.opentokSdkUrl;
                script.type = 'text/javascript';
                script.onreadystatechange = script.onload = resolve;
                head.appendChild(script);
            } else {
                reject();
            }
        }));
    }

    /**
     * Loads the Opentok Token and store it
     * @return {Promise.<{ApiKey: string, SessionId: string, Token: string}>} json response
     */
    getOpentokToken() {
        let apiUrl = this.config.injectedApiUrl + this.config.getOpentokTokenEndpoint;
        if (!apiUrl) return Promise.reject('apiURL is required');

        const isSafariParam = `?isSafariProject=${this.config.isSafari}`;
        apiUrl += isSafariParam;

        return fetch(apiUrl) // todo: plugin should not depend on sessionId!
            .then(response => response.json());
    }

    /**
     * Calls Opentok API endpoint with common parameters
     * @param {string} endpoint url of the API endpoint to call
     * @param {Object} params parameters to pass to the API endpoint
     * @return {Promise.<*>}
     */
    callOpentokAPI(endpoint, params) {
        const requestBodyParams = [];
        const url = `${this.config.injectedApiUrl}${endpoint}`;
        Object.keys(params).forEach((key) => {
            requestBodyParams.push(`${key}=${encodeURIComponent(params[key])}`);
        });

        const requestBody = requestBodyParams.join('&');
        return fetch(
            url, {
                method: 'post',
                body: requestBody,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            },
        ).then(response => response.json())
            .then((response) => {
                if (response.created) {
                    return response;
                }

                const errorMessage = `Opentok API call failed: url: ${url}`
                    + `recorder session id: ${params.sessionId} message: ${JSON.stringify(response)}`;
                return Promise.reject(errorMessage);
            });
    }

    /**
     * Call startRecording call on Opentok API
     * @param {Object.<{
     *      isSafari: boolean,
     *      recordingPath: string,
     *      spitRecordingOnResolutionChange: boolean
     * }>} recordingParameters Parameters for the recording backend
     * @return {Promise.<{
     *      Created: Date,
     *      Id: string,
     *      Name: string,
     *      OutputMode: string,
     *      SessionId: string,
     *      Status: string
     *  }>}
     *  json response
     */
    startRecording(recordingParameters) {
        return this.callOpentokAPI(
            this.config.startRecordingEndpoint, {
                ...recordingParameters,
                sessionId: this.otToken.sessionId,
            },
        );
    }

    /**
     * Call stopRecording call on Opentok API
     * @param {string} id GUID of the recording to stop
     * @param {Object.<{
     *      isSafari: boolean,
     *      recordingPath: string,
     *      spitRecordingOnResolutionChange: boolean
     * }>} recordingParameters Parameters for the recording backend
     * @return {Promise.<{
     *      Created: Date,
     *      Id: string,
     *      Name: string,
     *      OutputMode: string,
     *      SessionId: string,
     *      Status: string
     *  }>}
     *  json response
     */
    stopRecording(id, recordingParameters) {
        return this.callOpentokAPI(
            this.config.stopRecordingEndpoint, {
                recordingId: id,
                isSafari: recordingParameters.isSafari,
            },
        );
    }

    /**
     * Creates publisher element
     * @param {string} streamId The id of the stream publisher will be associated with
     * @param {boolean} video Sets whether to publish the video stream
     * @param {boolean} audio Sets whether to publish the audio stream
     * @return {Publisher}
     */
    createPublisher(video = true, audio = false, stream) {
        var publishOptions = {
            insertDefaultUI: false
        };
          
        if (this.config.publishOptions)
        {
            publishOptions = this.config.publishOptions;
        }

        if (stream) {            
            publishOptions.videoSource = stream.tracks.find(t => t.device.type == "video").device.id
            // Small hack to prevent OpenTok SDK from loading audio if it's not needed
            if (!audio) {
                publishOptions.audioSource = false;
            }
            else {
                publishOptions.audioSource = stream.tracks.find(t => t.device.type == "audio").device.id
            }
        }
        else    
        {  
            if (!audio) {
                publishOptions.audioSource = false;
            }        
        }

        // Small hack to prevent OpenTok SDK from loading video if it's not needed
        if (!video) {
            publishOptions.videoSource = false;
        }
        
        const publisher = OT.initPublisher(publishOptions);

        publisher.on('videoElementCreated', (vecEvent) => {
            if (this.config.isMobile && this.config.isSafari && !this.config.mirrorViewConfig) {
                vecEvent.element.style.position = 'absolute';
                vecEvent.element.style.width = '1px';
                vecEvent.element.style.height = '1px';

                document.body.appendChild(vecEvent.element);

                vecEvent.element.play();
            }
            else if (this.config.mirrorViewConfig && !(this.config.isMobile && this.config.isChrome)) {
                let styles = JSON.parse(this.config.mirrorViewConfig);

                let stlye = this.config.isMobile ? styles.mobile : styles.desktop;
                var mirrorViewHolder = document.createElement('div');
                var cameraCircle = document.createElement('div');

                Object.assign(mirrorViewHolder.style, stlye.mirrorViewHolder);
                Object.assign(cameraCircle.style, stlye.cameraCircle);
                Object.assign(vecEvent.element.style, stlye.mirrorVideo);

                mirrorViewHolder.appendChild(vecEvent.element);
                mirrorViewHolder.appendChild(cameraCircle);

                document.getElementById('resurveycontent').appendChild(mirrorViewHolder);
                vecEvent.element.play();
            }
        });

        return publisher;
    }

    /**
     * Helper function to start/connect opentok session
     * @param {MediaStream} stream The stream to publish
     * @return {Promise.<*|null>}
     */
    startSession(stream) {
        // resolve immediately if already connected
        if (this.session.connection && this.session.connection.connectionId) return Promise.resolve();
        
        return new Promise((resolve, reject) => {
            this.session.connect(this.otToken.token, (error) => {
                if (error) {
                    return reject(`Failed to connect opentok session. Code: ${error.code} Message: ${error.message}`);
                }
                this.publisher = this.createPublisher(true, this.config.recordAudio, stream);
                return this.session.publish(this.publisher, (err) => {
                    if (err) {
                        return reject(`Something went wrong: ${err}`);
                    }
                    return resolve();
                });
            });
        });
    }

    /**
     * Helper function to stop Opentok session
     * @return {Promise}
     */
    stopSession() {
        return new Promise((resolve, reject) => {
            try {
                this.session.unpublish(this.publisher);
                this.publisher = null;
                this.session.disconnect();
                resolve();
            } catch (e) {
                reject(`Something failed at OpentokService stopSession error: ${e}`);
            }
        });
    }
}
